import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Flag, Zap } from 'lucide-react';
import {
  CONTACT_FIELDS,
  CONTACT_SIGNAL_FIELD_KEYS,
  CONTACT_PLAY_FIELD_KEYS,
  USAGE_ROLE_KEY,
  type ContactRow,
} from '@shared/flow/contact-view';
import { RoleChips, coerceUsageRoles } from '../../shared/RoleChips';
import type { FilterGroup } from '@shared/flow/filter';
import { HIL_AWAITING_STATUS, SALESFORCE_SYNC_ACTOR } from '@shared/schemas/terra-signals';
import { CONTACT_ACCOUNT_CROSS_JOIN_FIELDS } from '@shared/flow/filter-fields';
import { accountInCommandCenter, contactsInCommandCenter } from '../../../../../../mini-apps/_shared/flow-links';
import type { FlowColumnDef } from '../types';
import { CompactUrlLink, LinkedInCell, parseDomainLink, sfdcRecordUrl } from '../links';
// U5: `fmtDate`/`score` were byte-identical local copies of the account
// table's; both tables now render through the one shared set in `../cells`,
// so an account field reads the same on a contact row as on an account row.
import { EmDash, fmtDate, fmtDateOnly, money, score, sanitizeRichHtml } from '../cells';
import { nonBlank } from '@shared/flow/non-blank';

/**
 * The static column config for `useContactTableModel` — moved out of that
 * file (thermo-nuclear review F1 Seam A: this is ~360 lines of module scope
 * with no hooks, module-scope-only pure config, not hook logic; the hook
 * file itself imports what it needs from here). A pure move, no behavior
 * change.
 */

/** Per-key render overrides on top of the shared field catalog. */
const RENDERERS: Record<string, (row: ContactRow) => React.ReactNode> = {
  // Clay Role (2026-09-25): a REAL catalog column read off the row, no
  // longer a synthetic column fed by the contact-state batch POST. The
  // endpoint folds its three `is_*` SQL booleans (the filterable fields under
  // the "Clay Role" picker group) into `row.usage_roles`, most significant
  // first; compact mode folds Builder into Key builder for the row. Re-parsed
  // at this boundary because validateResponse never throws: an unvalidated
  // payload must render the dash, not crash the cell.
  [USAGE_ROLE_KEY]: row => <RoleChips compact roles={coerceUsageRoles(row.usage_roles)} />,
  // The inline "⚡ In flow" name-chip (pre-existing, from #1046) is REMOVED —
  // the "Terra Actions" pill strip column (below) supersedes it with the same
  // signal via the in_flow dimension's pill. Do not re-add it here.
  name: row => (
    <span className="font-semibold text-content-primary dark:text-nightshade-100">
      {row.name ?? '—'}
    </span>
  ),
  // Account name → the Flow drill-down (QA-2 / V7, Spencer bug report: this
  // used to deep-link to the MAIN Terra account page, not Flow). SPA
  // navigation via react-router's <Link> — this cell only ever renders on
  // the GLOBAL Command Center mount (ACCOUNT_LEVEL_COLUMN_KEYS hides it on
  // every account-scoped mount, below), which is always inside the same
  // one-router SPA the Flow shell lives in, so a client-side `<Link>` is
  // always correct here (no bare `href` fallback needed). Uses the
  // `flow-links.ts` cross-app registry rather than hand-building the base —
  // same shape `AccountFlowTable`'s row click already navigates to.
  account_name: row => row.account_id && row.account_name ? (
    <Link
      to={accountInCommandCenter(row.account_id)}
      onClick={e => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300 hover:underline"
    >
      {row.account_name}
      <ExternalLink className="w-3 h-3 shrink-0" />
    </Link>
  ) : (row.account_name ?? <span className="text-content-faint dark:text-nightshade-600">—</span>),
  // Came From (2026-08-26, GTMOPS-2137 revision): the previous employer's
  // account — same link-building shape as account_name above, using the
  // wire-only `came_from_account_id` companion (contact-view.ts's doc
  // comment on that field). Blank for a contact with no tracked job change.
  came_from: row => row.came_from_account_id && row.came_from ? (
    <Link
      to={accountInCommandCenter(row.came_from_account_id)}
      onClick={e => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300 hover:underline"
    >
      {row.came_from}
      <ExternalLink className="w-3 h-3 shrink-0" />
    </Link>
  ) : (row.came_from ?? <span className="text-content-faint dark:text-nightshade-600">—</span>),
  // "Had Previous Contact" (2026-08-26, GTMOPS-2137 revision, renamed from
  // "Job Changer"): a bare true/false boolean read as internal jargon —
  // when true, link straight to the prior contact record instead (via the
  // wire-only `previous_job_contact_id` companion), matching came_from's
  // link-not-checkbox treatment. `contactsInCommandCenter` (not
  // `accountInCommandCenter`) since this is a different CONTACT record, not
  // necessarily the same account as `came_from`.
  previous_job_changer: row => row.previous_job_changer && row.previous_job_contact_id ? (
    <Link
      to={contactsInCommandCenter(row.previous_job_contact_id)}
      onClick={e => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300 hover:underline"
    >
      View previous contact
      <ExternalLink className="w-3 h-3 shrink-0" />
    </Link>
  ) : (row.previous_job_changer ? 'Yes, Not Mapped' : <span className="text-content-faint dark:text-nightshade-600">—</span>),
  // Went To (2026-08-27, Spencer) — the Alumni mirror of Came From, now real
  // table columns off the contacts row (the per-account /went-to endpoint is
  // gone; see CONTACT_FIELDS' Went To block in contact-view.ts). `new_title`
  // needs no renderer — FlowTable's default cell prints text as-is.
  new_employer: row => row.new_employer_account_id && row.new_employer ? (
    <Link
      to={accountInCommandCenter(row.new_employer_account_id)}
      onClick={e => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300 hover:underline"
    >
      {row.new_employer}
      <ExternalLink className="w-3 h-3 shrink-0" />
    </Link>
  ) : (row.new_employer ?? <span className="text-content-faint dark:text-nightshade-600">—</span>),
  // The successor CONTACT record itself. Lands on the plain account
  // drilldown (2026-08-28, Spencer — revises the 2026-08-26 Codex
  // adversarial-review decision below) rather than the account drilldown
  // with `?contact=` auto-expand, or the global `contactsInCommandCenter`
  // search: opening the successor's account context is the useful action
  // here, and forcing the roster into a filtered/scrolled contact view added
  // friction without much benefit. Prior reasoning this replaces: preferred
  // the account-scoped `contactInCommandCenter` (drills into the new
  // employer's account via `new_employer_account_id`) over the global
  // `contactsInCommandCenter` search, since the successor is generally not
  // an alumnus, so from the Alumni preset it's very likely NOT a row in the
  // global Contacts view's currently loaded/filtered set — that global link
  // reuses ContactFlowTable's own `?contact=` jump-handler, which toasts
  // "Contact isn't in the current view" instead of navigating when the row
  // isn't found (see the handler above). That `?contact=` jump-handler and
  // the global-search fallback below are UNCHANGED and still used when no
  // employer account id is available. Link TEXT is the successor's name when
  // we have it (strictly better than a static "View new contact"), which is
  // also what makes this column filterable/sortable on a meaningful value.
  new_contact: row => row.new_contact_id && row.new_contact ? (
    <Link
      to={row.new_employer_account_id
        ? accountInCommandCenter(row.new_employer_account_id)
        : contactsInCommandCenter(row.new_contact_id)}
      onClick={e => e.stopPropagation()}
      className="inline-flex items-center gap-1 text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300 hover:underline"
    >
      {row.new_contact}
      <ExternalLink className="w-3 h-3 shrink-0" />
    </Link>
  ) : (row.new_contact ?? <span className="text-content-faint dark:text-nightshade-600">—</span>),
  // Past Employer (Clay Customer) — real rich HTML from SFDC
  // (Past_Employers_Clay_Customers__c, e.g. `<ul><li><a href="...">Workday</a>
  // (workday.com) — Enterprise...</li></ul>`), same sanitize-and-render
  // treatment as Engagement Summary (AccountDetailPanels.tsx). Was rendering
  // as a raw unescaped string before this — QA catch, 2026-08-26.
  past_employers_clay_customers: row => row.past_employers_clay_customers ? (
    <span
      className="[&_a]:text-content-action [&_a]:dark:text-blueberry-400 [&_a]:hover:underline [&_ul]:list-none [&_ul]:p-0 [&_ul]:m-0"
      dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(row.past_employers_clay_customers) }}
    />
  ) : (<span className="text-content-faint dark:text-nightshade-600">—</span>),
  fit_score: row => score(row.fit_score),
  icp_fit_score: row => score(row.icp_fit_score),
  total_engagement_score: row => score(row.total_engagement_score),
  product_engagement_score: row => score(row.product_engagement_score),
  signals_engagement_score: row => score(row.signals_engagement_score),
  last_workspace_login: row => fmtDate(row.last_workspace_login),
  last_email_sent: row => fmtDate(row.last_email_sent),
  // last_activity / job_start_date are Postgres DATE columns: fmtDateOnly, not fmtDate (UTC-midnight shifts a day for US viewers).
  last_activity: row => fmtDateOnly(row.last_activity),
  lim_date: row => fmtDate(row.lim_date),
  // Raw Salesforce moment Type/Subtype; blank ('' or NULL) renders as an em dash. Display only (the stored value drives filter/sort/group).
  lim_type: row => nonBlank(row.lim_type) ?? <EmDash />,
  lim_subtype: row => nonBlank(row.lim_subtype) ?? <EmDash />,
  job_start_date: row => fmtDateOnly(row.job_start_date),
  last_gtme_email: row => fmtDate(row.last_gtme_email),
  last_claydr_email: row => fmtDate(row.last_claydr_email),
  last_gtme_meeting: row => fmtDate(row.last_gtme_meeting),
  gong_in_flow: row => (row.gong_in_flow ? <Zap className="w-3.5 h-3.5 text-content-action inline" /> : <span className="text-content-faint dark:text-nightshade-600">—</span>),
  // `LinkedInCell` (links.tsx, QA batch 1 D-F) — extracted from this exact
  // renderer (withProtocol, matching the detail card's LinkedInIcon: a raw
  // href breaks scheme-less values, the browser resolves "linkedin.com/in/x"
  // RELATIVE to the Terra origin — the exact row-vs-card discrepancy
  // reported 2026-07-24) so AccountFlowTable and the Signals table can share
  // it. Behavior here is byte-identical to before.
  linkedin: row => <LinkedInCell url={row.linkedin} />,
  sfdc_link: row => (
    <a
      href={sfdcRecordUrl('Contact', row.id)}
      target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
      className="text-content-action dark:text-blueberry-400 hover:text-content-action-hover dark:hover:text-blueberry-300"
    >
      <ExternalLink className="w-3.5 h-3.5 inline" />
    </a>
  ),
  // Raw Lightning URL → compact + clickable (Signal Link).
  lim_link: row => <CompactUrlLink url={row.lim_link} label="Signal" />,
  // "Last Interesting Moment" (lim_combined ← sc.last_interesting_moment) is a
  // SFDC HYPERLINK() formula → the value is an HTML anchor string, not a bare
  // URL, so it must be parsed (parseDomainLink) rather than printed raw —
  // otherwise the `<a href=…>` markup renders as literal escaped text. The
  // empty case (`<a href=" " target="_blank"> </a>`) parses to null (blank
  // href) → em-dash. Mirrors AccountFlowTable's email_domain_link renderer.
  // The link text is the LIM name (not the hostname), so prefer lim_name.
  lim_combined: row => {
    const parsed = parseDomainLink(row.lim_combined);
    if (!parsed) return <span className="text-content-faint dark:text-nightshade-600">—</span>;
    return (
      <a
        href={parsed.href} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
        title={row.lim_name ?? parsed.label}
        className="text-content-action dark:text-blueberry-400 hover:underline truncate inline-block max-w-[280px] align-bottom"
      >
        {row.lim_name ?? parsed.label}
      </a>
    );
  },
  // Account columns (U5) — rendered through AccountFlowTable's OWN helpers so
  // the same field reads identically on both tables. The remaining seven
  // (stage / industry / segment / tier / engagement status / country /
  // is_agency) fall through to FlowTable's default cell, which already
  // renders text as-is and booleans as Yes/No.
  account_arr: row => money(row.account_arr),
  account_open_pipeline: row => money(row.account_open_pipeline),
  account_number_of_open_opp: row => score(row.account_number_of_open_opp),
  account_propensity_score: row => score(row.account_propensity_score),
  account_f500_rank: row => score(row.account_f500_rank),
  account_number_of_employees: row => score(row.account_number_of_employees),
  // Signal Status (plan 2026-09-10-001 D2/D5) — the SCHEMA value, printed
  // as-is. There is no label translation here on purpose (rule 3: never show
  // a translated label where the rep filters on the raw value) — the one
  // piece of chrome is the HiL/Awaiting flag, which marks the single status
  // that means "a human has to do something": Terra fired the send but
  // Salesforce never produced a campaign member, so the row is stuck.
  signal_status: row => {
    if (row.signal_status == null) return <EmDash />;
    if (row.signal_status !== HIL_AWAITING_STATUS) return row.signal_status;
    return (
      <span
        className="inline-flex items-center gap-1 font-medium text-content-danger"
        title="Terra send fired but Salesforce has no campaign member yet — sync failure, needs a human."
      >
        <Flag className="w-3 h-3" aria-hidden />
        {row.signal_status}
      </span>
    );
  },
  // Same date treatment as every other date column (`last_activity` above) —
  // the boolean `has_recent_queue_send` the default chip filters on is
  // derived from this very column server-side (D4), so the two can't drift.
  last_queue_send_date: row => fmtDate(row.last_queue_send_date),
  // "Actioned By" (2026-09-22 plan) — who personally completed the action
  // (invited, sent) on this signal. Null → em dash (the default cell would
  // already do this, but the sentinel branch below needs its own renderer
  // regardless). The `SALESFORCE_SYNC_ACTOR` sentinel (a campaign member
  // added directly in Salesforce, never queued through Terra) reads as
  // "Unknown" — the raw sentinel is never rep-facing — with a tooltip
  // naming the raw value for anyone who inspects the cell.
  signal_completed_by: row => {
    if (row.signal_completed_by == null) return <EmDash />;
    if (row.signal_completed_by !== SALESFORCE_SYNC_ACTOR) return row.signal_completed_by;
    return <span title={SALESFORCE_SYNC_ACTOR}>Unknown</span>;
  },
  // `has_responded` needs no renderer: FlowTable's default cell already
  // prints a boolean as Yes/No and a null as an em dash (D5 — `null` on
  // every non-Campaign signal row is meaningful, not a false).
};

/**
 * Optional per-column HEADER tooltips (`FlowColumnDef.headerTitle`), kept
 * next to `RENDERERS` because they answer the same kind of question the
 * renderer does: what exactly is in this cell.
 */
const HEADER_TITLES: Record<string, string> = {
  // D2 — the `sig` derived table is scoped by the rep's own Signal filters
  // when they form exactly one AND group, and falls back to the contact's
  // newest signal row otherwise. A rep has no other way to learn that rule.
  signal_status: 'Status of the signal row matching your Signal filters; newest signal when unfiltered',
};

/**
 * Columns whose value is the ACCOUNT's, not the contact's — the account name
 * plus the 12 account columns (U5). Hidden on an account-scoped mount (see the
 * `config.columns` note below): every row there shares one account, so the
 * column would repeat a single value down the page.
 *
 * Derived from the shared cross-join catalog rather than re-listed, so adding
 * a 13th account field in `@shared/flow/filter-fields` hides it here too.
 *
 * `new_employer` (Went To, 2026-08-27) is deliberately NOT in this set despite
 * its value being an account name: it varies per ROW inside an account
 * drill-down (each alumnus went somewhere different), unlike `account_name`/
 * the cross-join fields, which are the same for every row on that mount —
 * exactly the case this hide exists to avoid duplicating down the page.
 */
const ACCOUNT_LEVEL_COLUMN_KEYS = new Set<string>([
  'account_name',
  ...CONTACT_ACCOUNT_CROSS_JOIN_FIELDS.map(f => f.key),
]);

/**
 * Group-header text for an F500 rank group: "Parent rank: #17". Null for an unranked account,
 * which falls to the table's "—" bucket (no invented label). The table keys a group by this
 * string, and it is 1:1 with the rank number (the server groups by rank ONLY), so two ranks never
 * merge and every account sharing a rank, subsidiaries included, is one group. No company name
 * is shown: rows keep their own account name in the Account column.
 */
export function f500GroupLabel(rank: number | null): string | null {
  if (rank == null) return null;
  return `Parent rank: #${rank}`;
}

/** `FlowTableConfig.groupValue` for contacts: remaps the F500 key, passes every other key through. */
export function contactGroupValue(row: ContactRow, key: string): unknown {
  if (key === 'account_f500_rank') return f500GroupLabel(row.account_f500_rank);
  return (row as Record<string, unknown>)[key];
}

export const CONTACT_COLUMNS: FlowColumnDef<ContactRow>[] = CONTACT_FIELDS.map(f => ({
  key: f.key,
  label: f.label,
  type: f.type,
  noFilter: f.noFilter,
  groupable: f.groupable,
  render: RENDERERS[f.key],
  numeric: f.type === 'number',
  headerTitle: HEADER_TITLES[f.key],
}));

/**
 * U5 (plan 2026-09-24-005, Item A) — while the request is NOT narrowed to one
 * play, a Signal column's header says why clicking it opens the "Pick which
 * play first" prompt instead of sorting. Prepended to (not replacing) an
 * existing D2 tooltip, so `signal_status` still explains which row it shows.
 */
const SIGNAL_UNNARROWED_HEADER_TITLE = 'Pick a play first to sort by this column';

export const CONTACT_COLUMNS_UNNARROWED: FlowColumnDef<ContactRow>[] = CONTACT_COLUMNS.map(c => (
  CONTACT_SIGNAL_FIELD_KEYS.includes(c.key)
    ? { ...c, headerTitle: c.headerTitle ? `${SIGNAL_UNNARROWED_HEADER_TITLE}. ${c.headerTitle}` : SIGNAL_UNNARROWED_HEADER_TITLE }
    : c
));

/**
 * U16 (plan 2026-09-24-005, D4) — the mount's column catalog: the base set
 * minus the Signal columns when `hideSignalColumns` (the "Not in this play
 * yet" mount), and the `play_*` columns ONLY when `showPlayColumns` — on
 * every other mount they are neither rendered nor offered in the picker (the
 * route 400s a `play_*` sort/group without `play_id`). Render-only, like
 * the drill-down's account-column hide: the view definition keeps its keys.
 *
 * The candidates mount used to also swap in a "Suggested" badge on the name
 * cell (`renderCandidateName`, RV5, plan 2026-09-24-005 U16) — removed
 * 2026-09-25 (PM simplification): the Play Status column's own "Suggested"
 * pill already carries that signal, so the badge was a redundant second copy
 * of the same value.
 */
/**
 * U2 (plan 2026-09-25-001) — `filters` minus every `play_*` condition, with
 * any group that leaves empty dropped. Query-time only (off the candidates
 * mount the route would 400 on them); the rep's chips are not rewritten.
 * Returns the same reference when nothing is stripped.
 */
export function withoutPlayFieldConditions(filters: FilterGroup[]): FilterGroup[] {
  if (!filters.some(g => g.conditions.some(c => CONTACT_PLAY_FIELD_KEYS.includes(c.field)))) return filters;
  return filters
    .map(g => ({ ...g, conditions: g.conditions.filter(c => !CONTACT_PLAY_FIELD_KEYS.includes(c.field)) }))
    .filter(g => g.conditions.length > 0);
}

export function mountColumns(
  base: FlowColumnDef<ContactRow>[],
  opts: { hideSignalColumns: boolean; showPlayColumns: boolean; hideAccountColumns: boolean },
): FlowColumnDef<ContactRow>[] {
  return base
    .filter(c => {
      if (!opts.showPlayColumns && CONTACT_PLAY_FIELD_KEYS.includes(c.key)) return false;
      if (opts.hideSignalColumns && CONTACT_SIGNAL_FIELD_KEYS.includes(c.key)) return false;
      if (opts.hideAccountColumns && ACCOUNT_LEVEL_COLUMN_KEYS.has(c.key)) return false;
      return true;
    });
}

/**
 * "Terra Actions" column — the contact-state read-model pill strip
 * (PR 2, 2026-07-23 decisions A1 + revision 2 rename; renamed again to
 * "Terra Actions" 2026-08-13, Spencer — same "user-visible text only" rule).
 * Not part of the shared CONTACT_FIELDS catalog (@shared/flow/contact-view):
 * its cell content is a client-side derived value (a batch-fetched pill map
 * keyed by contact id — see useContactStatePills, contact/hooks.ts), not a
 * field the /api/flow/contacts row itself carries, so it's built here
 * per-render instead of statically. noSort/noFilter: there's nothing on the
 * wire row to sort/filter by. The internal key/identifiers keep the
 * pre-rename "cross app state" name (only user-visible text changes) — only
 * CROSS_APP_STATE_LABEL's VALUE is "Terra Actions" now.
 *
 * `CROSS_APP_STATE_KEY` itself lives in `../views/syntheticColumns` — that's
 * the ONE place a wire-serialization boundary (`definitionOf`,
 * useViewStateMachine.ts) checks to strip it before a saved-view
 * create/update. See that file's doc comment for the full regression
 * writeup (2026-08-12: every Contact-entity Save 400'd with
 * `columns: unknown field "cross_app_state"`).
 */
export const CROSS_APP_STATE_LABEL = 'Terra Actions';

/**
 * Insert `key` right after `anchor` (or at the front if `anchor` is absent),
 * idempotently. One caller: the Terra Actions strip lands after 'name' on
 * every mount. (The play mount's second insert, SIGNAL_STATUS_KEY, is gone —
 * `signal_status` is a real catalog column now, plan 2026-09-10-001 D10.)
 */
export function insertAfter(columns: string[], anchor: string, key: string): string[] {
  if (columns.includes(key)) return columns;
  const idx = columns.indexOf(anchor);
  if (idx === -1) return [key, ...columns];
  return [...columns.slice(0, idx + 1), key, ...columns.slice(idx + 1)];
}
